/**
 * Marketing Manager Command Center (Task 205).
 *
 * The marketing-operations landing surface for the agency `marketing_manager`.
 * Rendered by `AppLanding` ONLY when `isMarketingManager(me)` is true AND the
 * owner gate did not fire first (owner precedence) — all other roles keep
 * their existing behavior. Display-only and gated upstream; it exposes NO
 * owner-management controls and does not weaken owner sovereignty.
 *
 * Design reference: design_handoff_super_agency/screens/24-marketing-manager-command-center.html
 * (recreated with the app's existing tokens + the shared `.occ-*` command-
 * center styles; not a blind paste). RTL, cream/ink/tangerine brand.
 *
 * Data posture (same honesty rule as the Owner Command Center): real data is
 * used where the existing `me-context` / `list-clients` surface already
 * provides it (clients-under-management count, the client list, status-derived
 * operational decisions). Marketing-operations metrics that have no live
 * endpoint yet (campaign health, content/production flow, team workload,
 * platform sync, escalations) render honest "—" placeholders / empty states
 * labelled «قيد الربط». No fabricated figures, no new endpoints, and every
 * action is a real route link (no faked escalations or destructive actions).
 */

import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import type { MeContext } from '../api/types';
import { useClientScope } from '../context/ClientScopeProvider';
import {
  SOON,
  avatarColor,
  buildOperationalDecisions,
  clientStatusLabelAr,
  initial,
  statusBadgeClass,
  statusRisk,
  statusWeight,
  toArabicDigits,
} from './marketingManagerCommandCenter.logic';

// ── Inline icon set (24×24, currentColor stroke) ─────────────────────
type IconName =
  | 'search'
  | 'plus'
  | 'bell'
  | 'eye'
  | 'megaphone'
  | 'trend-down'
  | 'clock'
  | 'check'
  | 'plug'
  | 'bolt'
  | 'chev'
  | 'calendar'
  | 'palette'
  | 'swap'
  | 'users'
  | 'arrow-up';

function Icon({ name, className }: { readonly name: IconName; readonly className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      aria-hidden="true"
    >
      {iconBody(name)}
    </svg>
  );
}

function iconBody(name: IconName): ReactNode {
  switch (name) {
    case 'search':
      return (
        <>
          <circle cx="11" cy="11" r="7" />
          <path d="m21 21-4.3-4.3" strokeLinecap="round" />
        </>
      );
    case 'plus':
      return <path d="M12 5v14M5 12h14" strokeLinecap="round" />;
    case 'bell':
      return (
        <path
          d="M6 8a6 6 0 1 1 12 0c0 7 3 9 3 9H3s3-2 3-9M13.7 21a2 2 0 0 1-3.4 0"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      );
    case 'eye':
      return (
        <>
          <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z" strokeLinejoin="round" />
          <circle cx="12" cy="12" r="3" />
        </>
      );
    case 'megaphone':
      return (
        <path
          d="M3 11v2a1 1 0 0 0 1 1h3l7 5V5L7 10H4a1 1 0 0 0-1 1zM18 8a5 5 0 0 1 0 8"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      );
    case 'trend-down':
      return (
        <path
          d="M3 7l7.5 7.5 4-4L21 17M21 17h-5M21 17v-5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      );
    case 'clock':
      return (
        <>
          <circle cx="12" cy="12" r="9" />
          <path d="M12 7v5l3 2" strokeLinecap="round" />
        </>
      );
    case 'check':
      return (
        <path
          d="M9 11l3 3 8-8M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      );
    case 'plug':
      return (
        <path
          d="M9 2v6M15 2v6M7 8h10v3a5 5 0 0 1-10 0zM12 16v6"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      );
    case 'bolt':
      return <path d="M13 2L3 14h8l-1 8 10-12h-8z" strokeLinejoin="round" />;
    case 'chev':
      return <path d="M15 18l-6-6 6-6" strokeLinecap="round" strokeLinejoin="round" />;
    case 'calendar':
      return (
        <>
          <rect x="3" y="5" width="18" height="16" rx="2" />
          <path d="M3 10h18M8 3v4M16 3v4" strokeLinecap="round" />
        </>
      );
    case 'palette':
      return (
        <path
          d="M12 3a9 9 0 1 0 0 18 3 3 0 0 0 2-5 3 3 0 0 1 2-5h1a4 4 0 0 0 4-4 9 9 0 0 0-9-4z"
          strokeLinejoin="round"
        />
      );
    case 'swap':
      return (
        <path
          d="M7 4L3 8l4 4M3 8h13M17 20l4-4-4-4M21 16H8"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      );
    case 'users':
      return (
        <>
          <circle cx="9" cy="8" r="4" />
          <path
            d="M3 21c0-3.3 2.7-6 6-6s6 2.7 6 6M17 11a4 4 0 0 0 0-8M22 21c0-2.8-1.9-5.1-4.5-5.8"
            strokeLinecap="round"
          />
        </>
      );
    case 'arrow-up':
      return <path d="M12 19V5M5 12l7-7 7 7" strokeLinecap="round" strokeLinejoin="round" />;
    default:
      return null;
  }
}

interface MarketingManagerCommandCenterProps {
  readonly me: MeContext;
}

export function MarketingManagerCommandCenter({ me }: MarketingManagerCommandCenterProps) {
  const { clients, status: scopeStatus } = useClientScope();
  const ready = scopeStatus === 'ready';
  const mmName = me.profile?.full_name ?? me.profile?.email ?? 'مديرة التسويق';
  const orgName = me.organization?.name ?? 'وكالتك';

  // Real: clients under management (everything not archived).
  const underManagement = clients.filter((c) => c.status !== 'archived').length;

  const decisions = buildOperationalDecisions(clients);

  // Client marketing-health rows — needs-attention first. Cap at 8.
  const tableRows = [...clients]
    .sort((a, b) => statusWeight(b.status) - statusWeight(a.status))
    .slice(0, 8);

  return (
    <div className="occ mmcc" dir="rtl">
      {/* ── Top bar ─────────────────────────────────────────────── */}
      <div className="occ-top">
        <div className="occ-search">
          <input type="search" placeholder="ابحث عن عميل، حملة، محتوى، مهمة…" aria-label="بحث" />
          <Icon name="search" />
        </div>
        <div className="occ-top-actions">
          <div className="occ-range" role="group" aria-label="النطاق الزمني">
            <button type="button" className="active" aria-pressed="true">
              ٧ أيام
            </button>
            <button type="button">هذا الشهر</button>
            <button type="button">الربع</button>
          </div>
          <Link to="/app/paid-media" className="occ-create">
            <Icon name="plus" />
            إجراء سريع
          </Link>
          <Link
            to="/app/notifications"
            className="occ-iconbtn"
            title="الإشعارات"
            aria-label="الإشعارات"
          >
            <Icon name="bell" />
            <span className="occ-dot" />
          </Link>
        </div>
      </div>

      {/* ── Page header ─────────────────────────────────────────── */}
      <header className="occ-head">
        <h1 className="h-page">
          مركز قيادة{' '}
          <em className="serif italic" style={{ color: 'var(--tangerine)' }}>
            التسويق
          </em>
        </h1>
        <p className="occ-sub">
          أهلاً {mmName} — مساحة {orgName}.{' '}
          {decisions.length > 0 ? (
            <>
              <b>{decisions.length} قرار تشغيلي</b> بحاجة إلى متابعتك اليوم.
            </>
          ) : (
            'لا توجد قرارات تشغيلية عاجلة الآن.'
          )}{' '}
          <span className="occ-note-inline">بعض مؤشّرات التشغيل قيد الربط بالبيانات المباشرة.</span>
        </p>
      </header>

      {/* ── Operations KPI strip (6) ────────────────────────────── */}
      <div className="occ-pulse">
        <span className="occ-blob" />
        <Kpi
          icon="eye"
          cap="عملاء تحت المتابعة"
          value={ready ? toArabicDigits(underManagement) : SOON}
          note={ready ? `من ${toArabicDigits(clients.length)} إجمالاً` : '…'}
        />
        <Kpi icon="megaphone" cap="حملات نشطة" value={SOON} note="قيد الربط" />
        <Kpi icon="trend-down" cap="حملات تحتاج تدخّل" value={SOON} note="قيد الربط" risk />
        <Kpi icon="clock" cap="محتوى متأخر" value={SOON} note="قيد الربط" risk />
        <Kpi icon="check" cap="موافقات عالقة" value={SOON} note="قيد الربط" />
        <Kpi icon="plug" cap="منصات غير متزامنة" value={SOON} note="قيد الربط" />
      </div>

      {/* ── Operational decisions centerpiece ───────────────────── */}
      <section className="occ-decisions">
        <div className="occ-dec-head">
          <span className="occ-dh-ic">
            <Icon name="bolt" />
          </span>
          <span className="occ-dh-t">
            قرارات تشغيلية تحتاج <em>إجراءك</em> اليوم
          </span>
          <span className="occ-dh-c">
            {decisions.length > 0 ? `${toArabicDigits(decisions.length)} بنود` : 'لا بنود'}
          </span>
        </div>
        {decisions.length > 0 ? (
          decisions.map((d) => (
            <div key={d.key} className={`occ-dec ${d.tone}`}>
              <span className={`occ-sev ${d.sev}`}>
                <span className="occ-sd" />
                {d.sevLabel}
              </span>
              <span className="occ-d-ic">
                <Icon name={d.icon} />
              </span>
              <div className="occ-d-body">
                <div className="occ-d-t">{d.title}</div>
                <div className="occ-d-why">{d.why}</div>
              </div>
              <Link to={d.to} className="occ-d-act">
                {d.action}
                <Icon name="chev" />
              </Link>
            </div>
          ))
        ) : (
          <div className="occ-empty">
            ستظهر هنا القرارات التشغيلية مرتّبة حسب الأولوية — الحملات المتعثّرة، الحسابات المنقطعة
            عن المزامنة، خطط المحتوى العاجلة، واختناقات الفريق — فور توفّر إشاراتها. لا يوجد ما
            يستدعي إجراءً عاجلاً الآن.
          </div>
        )}
      </section>

      {/* ── Main grid ───────────────────────────────────────────── */}
      <div className="occ-grid">
        <div>
          {/* Client marketing health */}
          <section className="occ-card">
            <div className="occ-card-head">
              <div className="occ-card-title">
                صحة <em>التسويق</em> للعملاء
              </div>
              <Link to="/app/clients" className="occ-card-link">
                كل العملاء <Icon name="chev" />
              </Link>
            </div>
            {tableRows.length > 0 ? (
              <table className="occ-ctable">
                <thead>
                  <tr>
                    <th>العميل</th>
                    <th className="c">الحملات</th>
                    <th className="c">المحتوى</th>
                    <th className="c">الإنتاج</th>
                    <th className="c">الخطر</th>
                    <th>الإجراء التسويقي</th>
                  </tr>
                </thead>
                <tbody>
                  {tableRows.map((c) => {
                    const risk = statusRisk(c.status);
                    return (
                      <tr key={c.id}>
                        <td>
                          <div className="occ-cl-cell">
                            <span className="occ-cl-av" style={{ background: avatarColor(c.id) }}>
                              {initial(c.name)}
                            </span>
                            <span>
                              <span className="occ-cl-nm">{c.name}</span>
                              <span className="occ-cl-sub">
                                <span className={`occ-badge ${statusBadgeClass(c.status)}`}>
                                  {clientStatusLabelAr(c.status)}
                                </span>
                              </span>
                            </span>
                          </div>
                        </td>
                        <td className="c occ-muted">{SOON}</td>
                        <td className="c occ-muted">{SOON}</td>
                        <td className="c occ-muted">{SOON}</td>
                        <td className="c">
                          <span className={`occ-risk ${risk.cls}`}>
                            <span className="rd" />
                            {risk.label}
                          </span>
                        </td>
                        <td>
                          <Link to={`/app/clients/${c.id}/overview`} className="occ-next">
                            فتح المساحة <Icon name="chev" />
                          </Link>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            ) : (
              <div className="occ-empty">
                {ready
                  ? 'لا يوجد عملاء بعد. أضيفي أول عميل لبدء متابعة صحته التسويقية هنا.'
                  : 'جارٍ تحميل قائمة العملاء…'}
              </div>
            )}
          </section>

          {/* Campaign exceptions */}
          <section className="occ-card">
            <div className="occ-card-head">
              <div className="occ-card-title">
                استثناءات <em>الحملات</em>
              </div>
              <Link to="/app/paid-media" className="occ-card-link">
                كل الحملات <Icon name="chev" />
              </Link>
            </div>
            <div className="occ-empty">
              تعرض هذه البطاقة <b>الاستثناءات فقط</b> — العائد المنخفض، الإنفاق الزائد، المزامنة
              المتأخّرة، المنصّات المنقطعة، وقفزات الميزانية — وليست لوحة حملات كاملة. ستظهر فور ربط
              مؤشّرات الأداء المباشرة.{' '}
              <Link to="/app/paid-media" className="occ-inline-link">
                افتحي الإعلانات المدفوعة ←
              </Link>
            </div>
          </section>

          {/* Content & production flow */}
          <section className="occ-card">
            <div className="occ-card-head">
              <div className="occ-card-title">
                تدفّق <em>المحتوى</em> والإنتاج
              </div>
              <Link to="/app/clients" className="occ-card-link">
                العملاء <Icon name="chev" />
              </Link>
            </div>
            <div className="occ-empty">
              سيظهر هنا المحتوى المتأخّر، الموافقات المعلّقة، مهام الإنتاج العالقة، أعمال الفيديو
              المتأخّرة، المنشورات القادمة، والتسليمات المحظورة — فور ربط بيانات المحتوى والإنتاج.
            </div>
          </section>
        </div>

        <div>
          {/* Team workload */}
          <section className="occ-card">
            <div className="occ-card-head">
              <div className="occ-card-title">
                حِمل <em>الفريق</em>
              </div>
              <Link to="/app/team" className="occ-card-link">
                الفريق <Icon name="chev" />
              </Link>
            </div>
            <div className="occ-team-roles">
              {[
                { icon: 'users' as IconName, tone: 'ic-sky', label: 'مديرو الحسابات' },
                { icon: 'megaphone' as IconName, tone: 'ic-amber', label: 'مشترو الإعلانات' },
                { icon: 'palette' as IconName, tone: 'ic-plum', label: 'المصممون' },
                { icon: 'eye' as IconName, tone: 'ic-sage', label: 'منتجو الفيديو' },
              ].map((r) => (
                <div key={r.label} className="occ-trole">
                  <span className={`occ-ti ${r.tone}`}>
                    <Icon name={r.icon} />
                  </span>
                  <span className="occ-tn">{r.label}</span>
                  <span className="occ-tover">{SOON}</span>
                </div>
              ))}
            </div>
            <div className="occ-empty sm">
              سيظهر هنا حِمل كل فريق، المهام المتأخّرة، مؤشّرات الاختناق، واقتراحات إعادة التوزيع
              فور ربط بيانات المهام.
            </div>
          </section>

          {/* Escalations to owner */}
          <section className="occ-card">
            <div className="occ-card-head">
              <div className="occ-card-title">
                تصعيد <em>للمالك</em>
              </div>
            </div>
            <div className="occ-empty sm">
              ستظهر هنا البنود التي تتجاوز صلاحياتك التشغيلية — مخاطر التجديد، زيادات الميزانية
              الكبيرة، الشكاوى الاستراتيجية، ومخاطر تسرّب العملاء — لرفعها إلى المالك عند ربط
              البيانات. لا تتضمّن هذه المساحة أي إدارة لحساب المالك.
            </div>
          </section>

          {/* Quick actions */}
          <div className="occ-qa-card">
            <div className="occ-qa-label">إجراءات سريعة</div>
            <div className="occ-qa-row">
              <Link to="/app/paid-media" className="occ-qa">
                <Icon name="megaphone" /> مراجعة حملة
              </Link>
              <Link to="/app/clients" className="occ-qa">
                <Icon name="calendar" /> تقويم المحتوى
              </Link>
              <Link to="/app/clients" className="occ-qa">
                <Icon name="check" /> الموافقات
              </Link>
              <Link to="/app/clients" className="occ-qa">
                <Icon name="palette" /> إسناد مهمة إنتاج
              </Link>
              <Link to="/app/team" className="occ-qa">
                <Icon name="swap" /> إعادة توزيع الحِمل
              </Link>
              <Link to="/app/paid-media" className="occ-qa">
                <Icon name="megaphone" /> الإعلانات المدفوعة
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Kpi({
  icon,
  cap,
  value,
  note,
  risk,
}: {
  readonly icon: IconName;
  readonly cap: string;
  readonly value: string;
  readonly note: string;
  readonly risk?: boolean;
}) {
  return (
    <div className="occ-pl">
      <div className="occ-cap">
        <Icon name={icon} />
        {cap}
      </div>
      <div className={`occ-big${risk ? ' risk' : ''}`}>{value}</div>
      <div className="occ-delta">{note}</div>
    </div>
  );
}
